import RangeDatePicker from '@/components/common/range-date-picker/RangeDatePicker';
import TableColumnsToggle from '@/components/common/table-columns-toggle/TableColumnsToggle';
import { TableFacetedFilter } from '@/components/common/table-faceted-filter/TableFacetedFilter';
import TableFilters from '@/components/common/table-filters/TableFilters';
import TableSearchInput from '@/components/common/tables/common/table-search-input/TableSearchInput';
import { Button } from '@/components/ui/button';
import useGetOrganizationServices from '@/hooks/queries/services-api/useGetOrganizationServices';
import useDateRange from '@/hooks/useDateRange';
import useStatus from '@/hooks/useStatus';
import { TServices } from '@/services/services';
import { Table } from '@tanstack/react-table';
import debounce from 'lodash/debounce';
import { Loader, XIcon } from 'lucide-react';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

type TableToolBarProps<TData> = Readonly<{
  table: Table<TData>;
  filtering: string;
  setFiltering: (value: string) => void;
  isFetching: boolean;
}>;

export default function TableToolBar<TData>({
  table,
  filtering,
  setFiltering,
  isFetching
}: TableToolBarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;

  const { t } = useTranslation();
  const { isPending, data: services } =
    useGetOrganizationServices<TServices[]>();
  const columnFiltersValue = table?.getState()?.columnFilters[0]
    ?.value as DateRange;
  const hasInitializedDate = useRef<boolean>(false);
  const [search, setSearch] = useState(filtering || '');

  const { statuses } = useStatus('reservations');

  const { date, setDate, resetDateRange, handleOnSelectDate } = useDateRange({
    column: table.getColumn('startTime')
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(
    debounce((value) => {
      setFiltering(value);
    }, 750),
    []
  );

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    debounceFn(event.target.value);
  };

  useEffect(() => {
    if (columnFiltersValue && !hasInitializedDate.current) {
      setDate(columnFiltersValue);
      hasInitializedDate.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFiltersValue]);

  return (
    <div className="flex flex-col items-start justify-between gap-2 py-4 md:flex-row">
      <div className="flex items-center w-full gap-2">
        <TableFilters filterActive={isFiltered}>
          <div className="flex flex-col items-start w-full gap-2">
            {table.getColumn('reservationStatus.name') && (
              <TableFacetedFilter
                column={table.getColumn('reservationStatus.name')}
                title={t('common.status.status')}
                options={statuses}
              />
            )}
            {table.getColumn('sale.saleItems.service.id') && (
              <TableFacetedFilter
                column={table.getColumn('sale.saleItems.service.id')}
                title={t('tables.common.activity')}
                options={
                  services?.length
                    ? services?.map((service) => {
                        return {
                          label: service.name,
                          value: service.id
                        };
                      })
                    : []
                }
                disabled={isPending || !services?.length}
              />
            )}
            {table.getColumn(t('startTime')) && (
              <div className="w-full">
                <RangeDatePicker
                  date={date}
                  onSelect={(value) => handleOnSelectDate(value)}
                />
              </div>
            )}
          </div>
        </TableFilters>
        {isFiltered && (
          <Button
            variant={'secondary'}
            onClick={() => {
              table.resetColumnFilters();
              resetDateRange();
            }}
          >
            {t('common.clearFilters')}
            <XIcon className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      <div className="flex flex-col-reverse items-start justify-center w-full gap-2 sm:flex-row md:w-auto">
        <div className="flex items-center w-full gap-3">
          <TableSearchInput value={search ?? ''} onChange={onSearch} />
          {search && isFetching && (
            <Loader className="text-gray-600 animate-spin" />
          )}
        </div>
        <TableColumnsToggle table={table} />
      </div>
    </div>
  );
}
